export const MOTHER_SUPPORT = {
  en: {
    seo: {
      title: "Postpartum & Motherhood Support in English | Rotterdam & Schiedam | Women's Den",
      description: "Private one-to-one support in English for women navigating the changes of early motherhood. A space to talk, reflect and make sense of what you are experiencing, online or in Schiedam.",
    },
    hero: {
      eyebrow: "You don't need to know exactly what you need before you come",
      title: "One-to-One Support for Mothers",
      sub: "A private space for when becoming a mother feels difficult, overwhelming or more complicated than you expected.",
      intro: "A 90-minute session where there is time to talk about what is happening, listen more closely to what you need, and find some ground within the changes of motherhood.",
      facts: ["90 minutes", "€110", "Online or in Schiedam", "In English"],
      primaryCta: "Book a session",
      secondaryCta: "What happens in a session",
    },
    recognition: {
      title: "Sometimes motherhood is harder than we expected",
      p1: "You can love your baby and still find becoming a mother enormously difficult.",
      p2: "Perhaps your birth didn't unfold as you hoped and something about the experience is still very present.",
      p3: "Perhaps you're exhausted by being needed so much of the time.",
      p4: "You may feel unlike yourself, overwhelmed by responsibility, unsure of what you need, or surprised by how much becoming a mother has changed your relationships, body, independence or everyday life.",
      p5: "Sometimes there isn't one particular problem. Everything has simply become a lot.",
      closing: "You don't have to wait until things become unbearable before asking for support.",
    },
    because: {
      title: "You might come because…",
      items: [
        "your birth experience is still very present and you need somewhere to talk about it",
        "motherhood feels more overwhelming than you expected",
        "you don't quite feel like yourself",
        "you're struggling with the constant demands of caring for a baby",
        "your relationship with your partner or family has changed",
        "you're finding it difficult to recognise or communicate your own needs",
        "you're carrying feelings about motherhood that are difficult to talk about elsewhere",
        "you simply know that you need some space for yourself",
      ],
      note: "A woman doesn't need to demonstrate that she is struggling enough to deserve support.",
    },
    session: {
      title: "Ninety minutes that belong to you",
      p1: "We begin with whatever feels most present for you.",
      p2: "There isn't a programme to follow, and you don't need to arrive knowing what you want to achieve.",
      p3: "There is time to talk, listen and stay with something rather than rushing towards an answer.",
      p4: "Depending on what you need, our session may also include quiet reflection, breathing, gentle movement or body awareness.",
      p5: "Together we can listen more closely to what is happening, make space for what has been difficult, and discover what might support you as you find your way through this transition — with care for yourself and for your family.",
      p6: "Sometimes what becomes clear may be practical. You might recognise that you need more help, a conversation with your partner, a different boundary, more rest or a change in how something is being carried at home.",
      p7: "At other times, what needs attention isn't something that can immediately be solved. There can also simply be value in having somewhere to say what is true.",
    },
    deeper: {
      title: "Becoming a mother changes more than our daily life",
      p1: "A baby arrives, but a mother is also coming into being.",
      p2: "This transition can bring us to edges in ourselves that we haven't met before.",
      p3: "Our relationship with our body may change. Our independence changes. Relationships can be rearranged. Things we once had space for may disappear for a while. Parts of ourselves may feel very close, while others become difficult to find.",
      pairs: ["There can be love alongside grief.", "Tenderness alongside anger.", "Gratitude alongside exhaustion.", "Confidence one day and uncertainty the next."],
      p5: "The session gives us space to listen more deeply to some of this unfolding, without deciding in advance what becoming a mother is supposed to look like for you.",
    },
    birth: {
      title: "When the birth is still with you",
      p1: "Sometimes birth remains very present after your baby has arrived.",
      p2: "Perhaps things happened quickly. Perhaps you felt frightened, disappointed, powerless or unheard. Perhaps the birth was simply very different from what you had imagined.",
      p3: "Or perhaps you don't have a clear word for what you're feeling. You simply find yourself returning to it.",
      p4: "There can be value in having time to tell the story at your own pace, to be heard, and to notice what still needs attention.",
    },
    testimonial: {
      text: "After a difficult birth, I needed somewhere I could speak honestly about what had happened and how I was finding life with a new baby. Irida gave me the time to tell my story without rushing me or trying to fix it. I felt deeply listened to. I felt lighter, clearer and less alone in everything I was carrying.",
      name: "Laura J.",
    },
    boundary: {
      title: "When other support may be needed",
      p1: "These sessions offer reflective and emotional support through the transition into motherhood. They are not psychotherapy or specialist treatment for birth trauma, postnatal depression or other mental-health conditions.",
      p2: "If something you're experiencing would be better supported by your GP, midwife, psychologist or another specialist, I'll encourage you to seek that care.",
      p3: "One-to-one support can also sit alongside professional healthcare when appropriate.",
    },
    single: {
      title: "Come when you need some space",
      p1: "You don't need to commit to a programme.",
      p2: "Each 90-minute session is booked individually.",
      p3: "You may come because there is something particular you want to explore, or simply because you've reached a point where you need some space for yourself.",
      p4: "If you later feel another session would be useful, you're welcome to book again. There is no expectation that you continue.",
    },
    baby: {
      title: "Ideally, this time is for you",
      p1: "If possible, I recommend having someone else care for your baby while we meet.",
      p2: "Having 90 minutes where you don't need to respond to your baby's immediate needs can give you more room to stay with your own experience.",
      p3: "But childcare isn't always possible, particularly with a young baby. If you don't have another option, your baby is welcome to be with you.",
      closing: "We work with what is possible that day.",
    },
    where: {
      title: "Meet in the way that works for you",
      options: ["Online", "In person in Schiedam"],
      or: "or",
    },
    irida: {
      heading: "Held by Irida",
      body: "I'm Irida Hysenbelli, founder of Women's Den. For years I have accompanied women through the transitions of their lives — through my coaching work, the women's spaces I facilitate, my work around pregnancy, birth and motherhood, and embodied practice. What I offer in these sessions is unhurried attention: somewhere you can say honestly how motherhood is actually going, and be heard.",
      link: "More about Irida",
    },
    crossLink: {
      title: "Looking for connection with other mothers?",
      body: "If what you're looking for is a regular place to meet other mothers and babies, the six-week Mums & Babies Circle offers a small, familiar group where we talk, move, rest and share some of the realities of early motherhood together.",
      cta: "Explore Mums & Babies Circle",
    },
    faq: {
      heading: "Questions",
      items: [
        { q: "Do I need to know what I want to talk about?", a: "No. You can arrive with something specific or simply with the sense that you need some support. We begin with what feels most present." },
        { q: "Can my baby come?", a: "Ideally, someone else cares for your baby so that the 90 minutes can be more fully yours. If this isn't possible, your baby is welcome." },
        { q: "Do I need to book several sessions?", a: "No. Sessions are booked individually. If you'd like to return for another session later, you can." },
        { q: "Can we meet online?", a: "Yes. Sessions are available online or in person in Schiedam." },
        { q: "Can I talk about a difficult birth?", a: "Yes. There is space to talk about your birth experience. These sessions are supportive rather than specialist birth-trauma therapy, and if you need additional professional care I'll encourage you towards appropriate support." },
      ],
    },
    final: {
      title: "One-to-One Support for Mothers",
      pull: "Sometimes becoming a mother asks for more than getting through the days. You need somewhere to bring what is happening to you.",
      rows: [
        ["Session", "90 minutes"],
        ["Investment", "€110"],
        ["Where", "Online or in person in Schiedam"],
        ["Language", "English"],
        ["Booking", "Individual session — no programme commitment"],
        ["Baby", "Ideally cared for by someone else; welcome if that isn't possible"],
      ],
      cta: "Book a session",
      secondary: "Have a question? Get in touch",
    },
  },
  nl: {
    seo: {
      title: "Engelstalige ondersteuning na de bevalling & bij moederschap | Rotterdam & Schiedam",
      description: "Besloten één-op-één ondersteuning in het Engels voor vrouwen die door de veranderingen van het vroege moederschap bewegen. Ruimte om te praten, te reflecteren en betekenis te geven aan wat je ervaart — online of in Schiedam.",
    },
    hero: {
      eyebrow: "Je hoeft niet precies te weten wat je nodig hebt voordat je komt",
      title: "Eén-op-één Begeleiding voor Moeders",
      sub: "Een plek voor jou als moeder worden moeilijk, overweldigend of ingewikkelder voelt dan je had verwacht.",
      intro: "Een sessie van 90 minuten waarin er tijd is om te praten over wat er speelt, stil te staan bij wat je nodig hebt en wat meer houvast te vinden in alles wat er met het moederschap verandert.",
      facts: ["90 minuten", "€110", "Online of in Schiedam", "In het Engels"],
      primaryCta: "Boek een sessie",
      secondaryCta: "Hoe ziet een sessie eruit",
    },
    recognition: {
      title: "Soms is het moederschap moeilijker dan we hadden verwacht",
      p1: "Je kunt van je baby houden en het moeder worden tegelijk enorm moeilijk vinden.",
      p2: "Misschien verliep je bevalling anders dan je had gehoopt en is iets van die ervaring nog heel aanwezig.",
      p3: "Misschien ben je uitgeput omdat er voortdurend een beroep op je wordt gedaan.",
      p4: "Misschien voel je je niet helemaal jezelf, ben je overweldigd door de verantwoordelijkheid of weet je niet goed wat je nodig hebt. Of misschien ben je verrast door hoeveel er door het moeder worden is veranderd — in je relaties, je lichaam, je onafhankelijkheid of je dagelijks leven.",
      p5: "Soms is er niet één bepaald probleem. Alles is gewoon veel geworden.",
      closing: "Je hoeft niet te wachten tot het ondraaglijk wordt voordat je om steun vraagt.",
    },
    because: {
      title: "Je zou kunnen komen omdat…",
      items: [
        "je bevallingservaring nog heel aanwezig is en je erover wilt praten",
        "het moederschap overweldigender voelt dan je had verwacht",
        "je je niet helemaal jezelf voelt",
        "je het zwaar vindt om voortdurend voor je baby te zorgen",
        "je relatie met je partner of familie is veranderd",
        "je het moeilijk vindt om je eigen behoeften te herkennen of uit te spreken",
        "je gevoelens over het moederschap hebt waar je ergens anders moeilijk over kunt praten",
        "je gewoon weet dat je ruimte voor jezelf nodig hebt",
      ],
      note: "Een vrouw hoeft niet aan te tonen dat ze genoeg worstelt om steun te verdienen.",
    },
    session: {
      title: "Negentig minuten die van jou zijn",
      p1: "We beginnen met wat er op dat moment het meest aanwezig is.",
      p2: "Er is geen programma dat we moeten volgen en je hoeft niet binnen te komen met een doel.",
      p3: "Er is tijd om te praten, te luisteren en stil te staan bij wat er speelt, zonder meteen naar een antwoord of oplossing te zoeken.",
      p4: "Afhankelijk van wat je nodig hebt, kan er in onze sessie ook ruimte zijn voor stille reflectie, ademhaling, zachte beweging of aandacht voor wat je in je lichaam ervaart.",
      p5: "Samen kunnen we beter luisteren naar wat er gebeurt, ruimte maken voor wat moeilijk is geweest en onderzoeken wat jou kan ondersteunen terwijl je je weg vindt in deze overgang — met aandacht voor jezelf en je gezin.",
      p6: "Soms wordt er iets heel praktisch duidelijk. Misschien merk je dat je meer hulp nodig hebt, een gesprek met je partner wilt voeren, een andere grens wilt stellen, meer rust nodig hebt of thuis iets anders wilt verdelen.",
      p7: "Op andere momenten vraagt iets aandacht dat niet meteen opgelost kan worden. Dan kan het op zichzelf al waardevol zijn om een plek te hebben waar je gewoon kunt zeggen hoe het werkelijk met je is.",
    },
    deeper: {
      title: "Moeder worden verandert meer dan ons dagelijks leven",
      p1: "Er wordt een baby geboren, maar ook een moeder.",
      p2: "Deze overgang kan kanten van onszelf naar boven brengen die we nog niet eerder zijn tegengekomen.",
      p3: "Onze relatie met ons lichaam kan veranderen. Onze onafhankelijkheid kan anders gaan voelen. Relaties kunnen verschuiven. Dingen waar we vroeger ruimte voor hadden, kunnen een tijd naar de achtergrond verdwijnen. Sommige delen van onszelf kunnen heel dichtbij voelen, terwijl andere moeilijker terug te vinden zijn.",
      pairs: ["Er kan liefde zijn naast verdriet.", "Tederheid naast boosheid.", "Dankbaarheid naast uitputting.", "Het ene moment vertrouwen en het andere onzekerheid."],
      p5: "In de sessie is er ruimte om aandacht te geven aan wat er in jou verandert, zonder vooraf te bepalen hoe het moeder worden er voor jou uit zou moeten zien.",
    },
    birth: {
      title: "Als de bevalling bij je blijft",
      p1: "Soms blijft de bevalling heel aanwezig nadat je baby is geboren.",
      p2: "Misschien ging alles snel. Misschien voelde je je bang, teleurgesteld, machteloos of ongehoord. Misschien was de bevalling gewoon heel anders dan je had gedacht.",
      p3: "Of misschien heb je geen duidelijk woord voor wat je voelt. Je merkt alleen dat je er in gedachten steeds naar teruggaat.",
      p4: "Het kan waardevol zijn om de tijd te hebben je verhaal in je eigen tempo te vertellen, gehoord te worden en te merken wat nog aandacht nodig heeft.",
    },
    testimonial: {
      text: "Na een moeilijke bevalling had ik een plek nodig waar ik eerlijk kon praten over wat er was gebeurd en hoe het leven met een pasgeboren baby voor mij was. Irida gaf me de tijd om mijn verhaal te vertellen, zonder me te haasten of te proberen het op te lossen. Ik voelde me echt gehoord. Daarna voelde ik me lichter, helderder en minder alleen in alles wat ik met me meedroeg.",
      name: "Laura J.",
    },
    boundary: {
      title: "Wanneer andere steun nodig kan zijn",
      p1: "Deze sessies bieden reflectieve en emotionele steun tijdens de overgang naar het moederschap. Ze zijn geen psychotherapie of specialistische behandeling voor geboortetrauma, postnatale depressie of andere geestelijke-gezondheidsklachten.",
      p2: "Als iets dat je ervaart beter ondersteund zou worden door je huisarts, verloskundige, psycholoog of een andere specialist, zal ik je aanmoedigen die zorg te zoeken.",
      p3: "Eén-op-één begeleiding kan ook naast professionele zorg bestaan wanneer dat passend is.",
    },
    single: {
      title: "Kom wanneer je ruimte nodig hebt",
      p1: "Je hoeft je niet voor een heel traject aan te melden.",
      p2: "Elke sessie van 90 minuten boek je afzonderlijk.",
      p3: "Je kunt komen omdat er iets specifieks is waar je bij stil wilt staan, of gewoon omdat je op een punt bent gekomen waarop je ruimte voor jezelf nodig hebt.",
      p4: "Als je later merkt dat een nieuwe sessie fijn of behulpzaam zou zijn, ben je welkom om opnieuw te boeken. Er is geen verwachting dat je meerdere sessies volgt.",
    },
    baby: {
      title: "Idealiter is deze tijd voor jou",
      p1: "Als het kan, raad ik aan dat iemand anders voor je baby zorgt terwijl wij samen zijn.",
      p2: "Negentig minuten waarin je even niet hoeft in te spelen op wat je baby nodig heeft, kan je meer ruimte geven om aandacht te hebben voor jezelf en wat er in jou speelt.",
      p3: "Maar opvang is niet altijd mogelijk, zeker niet met een jonge baby. Als je geen andere mogelijkheid hebt, is je baby welkom om erbij te zijn.",
      closing: "We werken met wat er die dag mogelijk is.",
    },
    where: {
      title: "Spreek af op de manier die voor jou werkt",
      options: ["Online", "In persoon in Schiedam"],
      or: "of",
    },
    irida: {
      heading: "Begeleid door Irida",
      body: "Ik ben Irida Hysenbelli, oprichter van Women's Den. Al jaren begeleid ik vrouwen rond belangrijke overgangen in hun leven — vanuit mijn ervaring met coaching, het begeleiden van vrouwengroepen, mijn werk rond zwangerschap, geboorte en moederschap, en lichaamsgerichte oefeningen. Wat ik in deze sessies bied, is tijd en aandacht zonder haast: een plek waar je eerlijk kunt vertellen hoe het moederschap werkelijk voor je is, en waar er naar je geluisterd wordt.",
      link: "Meer over Irida",
    },
    crossLink: {
      title: "Op zoek naar verbinding met andere moeders?",
      body: "Als je graag regelmatig andere moeders en baby's wilt ontmoeten, biedt de zesweekse Mums & Babies Circle een kleine, vertrouwde groep waarin we praten, bewegen, rusten en ervaringen uit de eerste maanden van het moederschap met elkaar delen.",
      cta: "Verken de Mums & Babies Circle",
    },
    faq: {
      heading: "Veelgestelde vragen",
      items: [
        { q: "Moet ik weten waar ik over wil praten?", a: "Nee. Je kunt aankomen met iets specifieks of gewoon met het gevoel dat je steun nodig hebt. We beginnen met wat er het meest aanwezig is." },
        { q: "Kan mijn baby mee?", a: "Idealiter zorgt iemand anders voor je baby, zodat de 90 minuten vollediger van jou kunnen zijn. Als dat niet mogelijk is, is je baby welkom." },
        { q: "Moet ik meerdere sessies boeken?", a: "Nee. Sessies worden los geboekt. Wil je later terugkomen voor een nieuwe sessie, dan kan dat." },
        { q: "Kunnen we online afspreken?", a: "Ja. Sessies zijn online mogelijk of in persoon in Schiedam." },
        { q: "Kan ik over een moeilijke bevalling praten?", a: "Ja. Er is ruimte om over je bevallingservaring te praten. Deze sessies zijn ondersteunend, geen specialistische geboortetraumatherapie — en als je aanvullende professionele zorg nodig hebt, zal ik je richting passende steun aanmoedigen." },
      ],
    },
    final: {
      title: "Eén-op-één Begeleiding voor Moeders",
      pull: "Soms vraagt moeder worden om meer dan de dagen doorkomen. Je hebt ergens nodig om te brengen wat er met je gebeurt.",
      rows: [
        ["Sessie", "90 minuten"],
        ["Investering", "€110"],
        ["Waar", "Online of in persoon in Schiedam"],
        ["Taal", "Engels"],
        ["Boeking", "Losse sessie — geen programmaverbintenis"],
        ["Baby", "Idealiter verzorgd door iemand anders; welkom als dat niet mogelijk is"],
      ],
      cta: "Boek een sessie",
      secondary: "Nog een vraag? Neem contact op",
    },
  },
};
